import React from 'react'
import { Link } from 'react-router-dom'
import { authPut } from '../../../../../shared/sso-workspace/auth-api'
import { StatusSwitch } from '../../../../../shared/sso-workspace/status-switch'
import { DomainPill } from '../../../../../shared/sso-workspace/pill'
import { domainTone } from '../../../../../shared/component/status-pill'
import { connectionName, domainsText, inUse, refusal } from './org-sso-testing'

// The connection's Status on its own page, for a connection the organization
// runs itself: the staff workspace's on/off control (status-switch.js),
// saved at once (auth's PUT /org-sso/connections/:id/active, recorded in the
// audit trail), so the organization rolls back without EducationXR.
// Switching off a connection that serves sign-ins asks first.
export const ConnectionStatusSwitch = ({ connection, enabled, onChanged }) => {
  const testers = Array.isArray(connection.testAllowlist) ? connection.testAllowlist.length : undefined

  const handleChange = async next => {
    try {
      const detail = await authPut(`org-sso/connections/${connection.id}/active`, { active: next })
      onChanged && (await onChanged(detail))
    } catch (err) {
      const refused = new Error(refusal(err, next ? "The connection couldn't be switched on." : "The connection couldn't be switched off."))
      refused.failure = err
      throw refused
    }
  }

  return (
    <div data-testid="connection-status">
      <StatusSwitch id="connection-active" label="Status" active={Boolean(connection.active)} disabled={!enabled}
        onChange={handleChange} testers={testers} connectionName={connectionName(connection)} domains={domainsText(connection)}
        servingNow={Boolean(connection.active) && inUse(connection)}
        undo="To undo, choose Active again. EducationXR doesn't need to do anything." />
    </div>
  )
}

// Domains & organizations on the organization's connection page: the
// organization's own domains bound to the connection (auth sends only its
// own), with where to bind or unbind them.
export const ConnectionDomains = ({ connection, selfServe }) => {
  const domains = connection.domains || []
  return (
    <div className="d-grid gap-2" data-testid="connection-domains">
      {domains.length === 0
        ? <p className="sw-hint m-0">No domains of yours are bound to it yet.</p>
        : <ul className="sc-rows">
          {domains.map(d =>
            <li key={d.id} className="sc-row">
              <code className="fw-semibold">{d.domain}</code>
              <DomainPill tone={domainTone(d)} />
            </li>)}
        </ul>}
      <p className="sw-hint m-0">
        {selfServe
          ? <React.Fragment>Claim, verify, bind and unbind domains on your <Link to="/org/sso#domains">Authentication page</Link>. People who sign in through it join your organization.</React.Fragment>
          : 'People at these domains are sent to this connection. Contact support to change it.'}
      </p>
    </div>
  )
}

// Claims & mapping on the organization's connection page: the role mappings
// that give your people a role from what the identity provider says about
// them. Read only here.
export const ConnectionClaims = ({ mappings }) => {
  const rows = mappings || []
  return (
    <div className="d-grid gap-2" data-testid="connection-claims">
      {rows.length === 0
        ? <p className="sw-hint m-0">No role mappings. Everyone who signs in gets your organization's default role.</p>
        : <ul className="sc-rows">
          {rows.map(m =>
            <li key={m.id} className="sc-row">
              <div className="sc-row-main">
                <span className="fw-semibold"><code>{m.claimName}</code> is <code>{m.claimValue}</code></span>
                <span className="sw-hint">Gives the role {m.roleName}{m.label ? ` · ${m.label}` : ''}</span>
              </div>
            </li>)}
        </ul>}
      <p className="sw-hint m-0">To add or change a role mapping, contact support.</p>
    </div>
  )
}
